import { BoundingBoxAA, GBasePass, GetBasePassUniformParametersList, Mesh } from "../../../basePass";
import { GUserInputDesc } from "../../../core/input";
import { GRenderTargets } from "../../../core/renderTargets";
import { BindRenderTarget } from "../../../core/resourcesUtils";
import { GScreenDesc } from "../../../core/screenDesc";
import { Shader } from "../../../core/shaderUtils";
import { GTexturePool } from "../../../core/texturePool";
import { GStack, GetVec2, GetVec3, Vector3 } from "../../../core/types";
import { GTime, MathClamp, MathLerp, MathMapToRange } from "../../../core/utils";
import { GSimpleShapesRenderer } from "../../../helpers/shapeRender";
import { GetShaderSourceBasePassMainPS, GetShaderSourceBasePassMainVS } from "../../../shaders/shaderBasePass";
import { ShaderSourceFullscreenPassVS } from "../../../shaders/shaderPostProcess";
import { GetShaderSourceDestrSceneVolumeLightRenderPS, GetShaderSourceTVScenePS } from "../../../shaders/shaderSDF";
import { CImpactComponentBase } from "../../../tools/tools";
import { GVectorFieldInjector, VectorField } from "../../../vectorField";
import { GSceneDesc, SceneBase } from "../../scene";
import { GetSDFRenderUniformParametersList, SDFRender } from "../../sdf";
import { EBulletTimeState, GBulletSystem } from "./bullets";
import { DestrPaintPlane } from "./destrPaintingPlane";
import { DestructionParticles } from "./destrParticles";
import { Decals, DestructionTiles } from "./destrTiles";
import {
    GetDustParticleDesc,
    GetFlashParticleDesc,
    GetImpactSmokeParticleDesc,
    GetImpactSparksParticleDesc,
    GetPaintSmokeParticleDesc,
    GetPaintSparksParticleDesc,
    GetPaintSparksParticleDesc2,
    GetSmokeParticleDesc,
    GetSnowParticleDesc,
    GetSnowSmokeParticleDesc,
    GetSparksParticleDesc,
    GetWoodDebrisParticleDesc,
    GetWoodMesh2ParticleDesc,
    GetWoodMeshParticleDesc,
    GetWoodSmallChunkParticleDesc,
} from "./particleDesc";
import { GCameraShakeController } from "../../../animationController";
import { RCombinerPass } from "../../../postprocess";
import { DestrPaintOnUpdateScript, PaintDestrImpactComponent } from "./destrPaintingScript";
import { GLBindUniformBuffer, GLSetTexture, GLSetTextureCubemap } from "../../../helpers/glHelper";
import { CommonRenderingResources } from "../../../shaders/shaderConfig";
import { SpatialControlPoint } from "../../../core/spatialController";
import { GAudioEngine, SoundSample } from "../../../core/audioEngine";
import { PayneSceneLightSwitch } from "../lightSwitch/lightSwitch";
import { GHallwaySceneCSS } from "../hallway/hallwayCSS";

export function InitSDFTVScene() {
    return /* glsl */ `

	#define LAMP_LIGHTS
	#define DESTR_PAINTING
	#define TEXTURE_MASK
	#define USE_LIGHTPROBES

	#ifndef SDF_MAX_DISTANCE
	#define SDF_MAX_DISTANCE 20.0
	#endif


	float sceneSDF(vec3 curPos, bool bRayMeshIntersection, bool bRayMeshIntersection2)
	{
		float curD = SDF_MAX_DISTANCE;

		

		return curD;
	}

	float SDFComputeShadow(vec3 p, vec3 lightPos)
	{
		float res = 1.0;

		return res;
	}

	`;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function InitSDFDestrPaintingScene(scene: PayneSceneDestrPainting) {
    return /* glsl */ `

	#define LAMP_LIGHTS
	#define DESTR_PAINTING
	#define TEXTURE_MASK
	#define USE_LIGHTPROBES

	#ifndef SDF_MAX_DISTANCE
	#define SDF_MAX_DISTANCE 20.0
	#endif


	float sceneSDF(vec3 curPos, bool bRayMeshIntersection, bool bRayMeshIntersection2)
	{
		float curD = SDF_MAX_DISTANCE;

		//main painting pos
		/* {
			//vec3 boxPos = vec3(0.0, 0.0, -1.0);
			vec3 boxPos = SceneBuffer.OccluderPlaneCenter;
			vec3 boxExtent = SceneBuffer.OccluderPlaneExtent;
			curD = min(curD, sdBox(curPos, boxPos, boxExtent));
		} */

		//painting frame
		{
			const vec3 pExtent = vec3(2.2, 2.7 * 0.96, 0.25);
			const vec3 pCenter = vec3(0.0, 3.0, 0.1);

			curD = min(curD, sdBox(curPos, pCenter + vec3(0.0, pExtent.y, 0.0), vec3(pExtent.x, pExtent.y * 0.04, pExtent.z)));
			curD = min(curD, sdBox(curPos, pCenter - vec3(0.0, pExtent.y, 0.0), vec3(pExtent.x, pExtent.y * 0.04, pExtent.z)));

			curD = min(curD, sdBox(curPos, pCenter - vec3(pExtent.x * 0.96, 0.0, 0.0), vec3(pExtent.x * 0.04, pExtent.y * 0.975, pExtent.z)));
			curD = min(curD, sdBox(curPos, pCenter + vec3(pExtent.x * 0.96, 0.0, 0.0), vec3(pExtent.x * 0.04, pExtent.y * 0.975, pExtent.z)));
		}

		//name slab
		{
			curD = min(curD, sdBox(curPos, vec3(0.0, -0.13, -0.02), vec3(5.0 * 0.15, 1.0 * 0.15, 0.1)));
		}

		//lamp sphere
		{
			curD = min(curD, sdSphere(curPos, vec3(-4.8, 5.3, -0.17), 0.7));
			curD = min(curD, sdSphere(curPos, vec3(4.8 + 0.85, 5.3, -0.17), 0.7));
		}

		//walls
		//front
		{
			curD = min(curD, /* abs */(0.0 - curPos.z));
		}
		

		return curD;
	}

	float SDFComputeShadow(vec3 p, vec3 lightPos)
	{
		float res = 1.0;

		//Compute self-shadow bias
		float bias = 0.0;
		
		float d = length(lightPos - p);
		vec3 l = (lightPos - p) / d;

		float maxT = d;

		float w = 0.1;
		float minT = 0.01;
		float t = minT;

	#if 1 //compute self intersection bias
		float sdf = SDF_MAX_DISTANCE;
		sdf = min(sdf, sceneSDF(p, false, false));

		float selfIntersectThres = 0.01;

		if(sdf < selfIntersectThres)
		{
			bias = abs(sdf) + 0.01;
		}
	#endif

		float ph = 1e20;
		for( int i=0; i<16; i++ )
		{
			//float h = sceneSDF(p + l*t, bRayMeshIntersection);
			vec3 curPos = p + l*t;
			float h = SDF_MAX_DISTANCE;

			h = min(h, sceneSDF(curPos, false, false));

			h += bias;

			res = min( res, h/(w*t) );
		
			t += h;
		
			if( res<0.01 || t>maxT ) break;
		}

		res = clamp( res, 0.0, 1.0 );
		return res*res*(3.0-2.0*res);

		//return res;

	}

	`;
}

export enum EPaintingState {
    InitNoHeal = 0,
    HealProtect = 1,
    HeartVisible = 2,
    HeartCanShoot = 3,
    StartDeath = 4,
    Dead = 5,
    AfterDeath = 6,
}

export class PayneSceneDestrPainting extends SceneBase {
    WallTiles;
    WallDecals;
    DustParticles;
    FlashParticles;
    SmokeParticles;

    WoodDebrisParticles;
    WoodSmallChunkParticles;
    WoodMeshParticles;
    WoodMeshParticles2;
    SparksParticles;

    SnowParticles;
    //SnowFastParticles;
    SnowSmokeParticles;

    ImpactSmokeParticles;
    ImpactSparksParticles;

    PaintSmokeParticles;
    PaintSparksParticles;
    PaintSparksParticles2;

    ImpactComponent: CImpactComponentBase;
    ImpactComponentPainting: PaintDestrImpactComponent;

    SceneSDFDescShootingScene: string | null = null;
    SceneSDFDescTVScene: string | null = null;

    MeshDestrPaniting: Mesh[] = [];
    MeshLampRightArr: Mesh[] = [];
    MeshLampLeftArr: Mesh[] = [];

    //DestrHotlineCommonDesc
    bLeftLampDestroyed = false;
    bRightLampDestroyed = false;
    LampLeftAABB = new BoundingBoxAA();
    LampRightAABB = new BoundingBoxAA();
    PaintingAABB = new BoundingBoxAA();
    PaintingFrameAABB = new BoundingBoxAA();

    WallMetalStripAABB1 = new BoundingBoxAA();
    WallMetalStripAABB2 = new BoundingBoxAA();

    SDFSceneRender: SDFRender;

    PaintingPlane: DestrPaintPlane;

    CityTexture: WebGLTexture;
    FaceTexture: WebGLTexture;
    bMovieTitleShown = false;
    MovieTitleTexture: WebGLTexture;
    MovieTitleSloganTexture: WebGLTexture;
    PaintProtectRune: WebGLTexture;
    RuneBrightnessScale = 1.0;

    SirenSwitchTimeLast: number[] = [];
    SirenRed: boolean[] = [];

    PaintDensityBrightness = 1.0;

    //Heart
    HeartHeightScale = 2.25;
    HeartTexName = "heart225";
    HeartExtentScale = 0.65;
    /* HeartExtentScale = 0.85;
    HeartHeightScale = 1.64;
    HeartTexName = "heart164"; */
    /* HeartExtentScale = 0.95;
    HeartHeightScale = 1.55;
    HeartTexName = "heart155"; */
    /* HeartExtentScale = 0.7;
    HeartHeightScale = 2.03;
    HeartTexName = "heart203"; */

    MessageMesh;
    HeartMesh: Mesh[] = [];
    HeartCenterPos = new Vector3(6.5, 1.81, 0.02);
    HeartExtent = new Vector3(this.HeartExtentScale, this.HeartExtentScale, 0.1);

    //State
    PaintState: EPaintingState = EPaintingState.InitNoHeal;

    bTVRenderState = false;
    TVTransitionParam = 1.0;
    TVTransitionParam2 = 1.0;
    ShaderTVScene: Shader;
    TVSwitchButtonController: SpatialControlPoint;
    ETVSwitchState = 0; //0:on 1:hover 2:clicked

    //Sound
    SoundIngram: SoundSample = new SoundSample();
    SoundBulletTimeStart: SoundSample = new SoundSample();
    SoundBellHit: SoundSample = new SoundSample();
    SoundWitchScream: SoundSample = new SoundSample();
    SoundBulletTimeEnd: SoundSample = new SoundSample();

    SoundTVSwitch: SoundSample = new SoundSample();
    SoundMainTheme0: SoundSample = new SoundSample();
    NumTimesMainThemePlayed = 0;
    SoundMainTheme1: SoundSample = new SoundSample();

    SoundLampHit: SoundSample = new SoundSample();
    SoundDefaultHitsArr: SoundSample[] = [];
    SoundWoodHitsArr: SoundSample[] = [];
    SoundWoodDebrisArr: SoundSample[] = [];
    SoundWoodFallArr: SoundSample[] = [];
    SoundPaintHitsArr: SoundSample[] = [];
    SoundPaintSparksArr: SoundSample[] = [];

    SoundDepth = 1.0;
    SoundWidth = 1.0;
    SoundGain = 4.0;
    SoundIndex = 0;

    SoundOsc0: OscillatorNode | null = null;
    SoundOsc1: OscillatorNode | null = null;
    SoundOscGain: GainNode | null = null;
    SoundOscGainInitValue = 0.1;
    SoundOscFilter: BiquadFilterNode | null = null;
    SoundOscGainScaleParam = 1.0;

    SoundBackGain: GainNode | null = null;
    bBackFilterEnabled = false;
    TimeSinceBackFilterEnabled = 0.0;
    SoundBackFilter: BiquadFilterNode | null = null;

    SoundTVFilter: BiquadFilterNode | null = null;

    bSynthSoundPlaying = false;
    StartSynthSound() {
        if (!this.bSynthSoundPlaying) {
            this.SoundOsc0?.start();
            this.SoundOsc1?.start();
            this.bSynthSoundPlaying = true;
        }
    }
    StopSynthSound() {
        if (this.bSynthSoundPlaying) {
            this.SoundOsc0?.stop();
            this.SoundOsc1?.stop();
        }
    }

    bBackFilterEnableFromStart = Math.random() < 0.45;
    SoundEnableBackFilter() {
        if (this.SoundBackFilter && !this.bBackFilterEnabled) {
            this.SoundBackFilter.type = "lowpass";
            this.SoundBackFilter.frequency.value = 20000;
            this.SoundBackFilter.Q.value = 2;

            this.bBackFilterEnabled = true;
        }
    }

    async InitOsc() {
        const audioEngine = GAudioEngine.Instance!;
        if (audioEngine.Context) {
            // Create the first oscillator (base tone)
            const oscillator1 = audioEngine.Context.createOscillator();
            oscillator1.type = "sine"; // Base waveform
            oscillator1.frequency.value = 50; // Base frequency in Hz

            // Create the second oscillator (octave higher)
            const oscillator2 = audioEngine.Context.createOscillator();
            oscillator2.type = "square"; // Same waveform as oscillator1
            oscillator2.frequency.value = 60; // One octave higher

            this.SoundOsc0 = oscillator1;
            this.SoundOsc1 = oscillator2;

            // Create a low-pass filter
            const filter = audioEngine.Context.createBiquadFilter();
            filter.type = "lowpass";
            filter.frequency.value = 250; // Initial cutoff frequency
            filter.Q.value = 16; // Resonance for warmth
            this.SoundOscFilter = filter;

            // Create a gain node to control amplitude
            const gainNode = audioEngine.Context.createGain();
            gainNode.gain.value = this.SoundOscGainInitValue; // Initial volume

            this.SoundOscGain = gainNode;

            // Connect both oscillators to the gain node
            oscillator1.connect(gainNode);
            oscillator2.connect(gainNode);

            // Connect the gain node to the filter, and the filter to the destination
            gainNode.connect(filter);
            filter.connect(audioEngine.MasterGain!);

            // Start all oscillators and LFOs
            /* oscillator1.start();
            oscillator2.start(); */
        }
    }

    bViolinPannersConnected = false;
    SoundsViolinConnectPanner() {
        if (!this.bViolinPannersConnected) {
            const audioContext = GAudioEngine.Instance!.Context!;

            // Create a GainNode to control volume for each channel
            const leftGain = audioContext.createGain();
            const rightGain = audioContext.createGain();

            // Reduce the volume of each channel to 50% to avoid clipping
            leftGain.gain.value = 0.5;
            rightGain.gain.value = 0.5;

            // Create a ChannelMerger to combine the channels into one mono channel
            const merger = audioContext.createChannelMerger(1);

            // Create a ChannelSplitter to separate the stereo channels
            const splitter = audioContext.createChannelSplitter(2);

            // Connect the splitter channels to the gain nodes
            splitter.connect(leftGain, 0); // Left channel
            splitter.connect(rightGain, 1); // Right channel

            // Combine the channels by connecting them to the merger
            leftGain.connect(merger, 0, 0);
            rightGain.connect(merger, 0, 0);

            // Connect the source to the splitter and start
            this.SoundMainTheme0.Gain!.disconnect();
            this.SoundMainTheme0.Gain!.connect(splitter);
            this.SoundMainTheme1.Gain!.disconnect();
            this.SoundMainTheme1.Gain!.connect(splitter);

            this.SoundTVFilter!.Q.value = 4.0;
            merger.connect(this.SoundTVFilter!);

            this.bViolinPannersConnected = true;
        }
    }

    async LoadSounds() {
        const audioEngine = GAudioEngine.Instance!;

        //audioEngine.CreateImpulseResponse(1.5, 10);
        audioEngine.CreateImpulseResponse(2.5, 20);
        //audioEngine.ConvolverGain.connect(this.MasterGain);

        if (audioEngine.Context && audioEngine.MasterGain) {
            this.SoundBackFilter = audioEngine.Context.createBiquadFilter();
            this.SoundBackFilter.type = "lowshelf";
            this.SoundBackGain = audioEngine.Context.createGain();

            this.SoundBackFilter.connect(this.SoundBackGain);
            this.SoundBackGain.connect(audioEngine.MasterGain);

            let mainNode = audioEngine.MasterGain;
            mainNode = this.SoundBackFilter;

            if (audioEngine.ConvolverGain) {
                audioEngine.ConvolverGain.connect(this.SoundBackFilter);
            }

            this.SoundBulletTimeStart.Init(audioEngine.Context, "slowStart.mp3", audioEngine.MasterGain, 0.4);
            this.SoundBellHit.Init(audioEngine.Context, "bellHit.mp3", audioEngine.MasterGain, 0.4);
            this.SoundWitchScream.Init(audioEngine.Context, "witchScream.mp3", audioEngine.MasterGain, 0.4);
            this.SoundBulletTimeEnd.Init(audioEngine.Context, "slowEnd.mp3", mainNode, 0.4);

            this.SoundTVSwitch.Init(audioEngine.Context, "tvSwitch.mp3", audioEngine.MasterGain, 0.4);

            {
                this.SoundTVFilter = audioEngine.Context.createBiquadFilter();
                this.SoundTVFilter.type = "highpass";
                this.SoundTVFilter.frequency.value = 0;
                this.SoundTVFilter.connect(audioEngine.MasterGain);

                this.SoundMainTheme0.Init(audioEngine.Context, "violin0.mp3", audioEngine.MasterGain, 0.0);
                this.SoundMainTheme1.Init(audioEngine.Context, "violin1.mp3", audioEngine.MasterGain, 0.9);
                //this.SoundsViolinConnectPanner(audioEngine.MasterGain);
            }

            {
                const defHitNames = ["hitDef0.mp3", "hitDef1.mp3", "hitDef2.mp3"];
                for (let i = 0; i < defHitNames.length; i++) {
                    this.SoundDefaultHitsArr[i] = new SoundSample();
                    await this.SoundDefaultHitsArr[i].Init(
                        audioEngine.Context,
                        defHitNames[i],
                        audioEngine.MasterGain,
                        0.4,
                    );

                    if (this.SoundDefaultHitsArr[i].Gain && audioEngine.Convolver) {
                        this.SoundDefaultHitsArr[i].CreatePanner(audioEngine.Context, mainNode);
                        this.SoundDefaultHitsArr[i].Pan!.connect(audioEngine.Convolver);
                        //this.SoundDefaultHitsArr[i].Gain!.connect(audioEngine.Convolver);
                    }
                }

                for (let i = 0; i < 8; i++) {
                    this.SoundWoodHitsArr[i] = new SoundSample();
                    await this.SoundWoodHitsArr[i].Init(
                        audioEngine.Context,
                        `woodHit` + i + `.mp3`,
                        audioEngine.MasterGain,
                        1.0,
                    );

                    if (this.SoundWoodHitsArr[i].Gain && audioEngine.Convolver) {
                        this.SoundWoodHitsArr[i].CreatePanner(audioEngine.Context, mainNode);
                        this.SoundWoodHitsArr[i].Pan!.connect(audioEngine.Convolver);
                        //this.SoundWoodHitsArr[i].Gain!.connect(audioEngine.Convolver);
                    }
                }

                for (let i = 0; i < 7; i++) {
                    this.SoundWoodDebrisArr[i] = new SoundSample();
                    await this.SoundWoodDebrisArr[i].Init(
                        audioEngine.Context,
                        `woodDebris` + i + `.mp3`,
                        audioEngine.MasterGain,
                        1.0,
                    );

                    if (this.SoundWoodDebrisArr[i].Gain && audioEngine.Convolver) {
                        this.SoundWoodDebrisArr[i].CreatePanner(audioEngine.Context, mainNode);
                        this.SoundWoodDebrisArr[i].Pan!.connect(audioEngine.Convolver);
                        //this.SoundWoodHitsArr[i].Gain!.connect(audioEngine.Convolver);
                    }
                }

                for (let i = 0; i < 4; i++) {
                    this.SoundWoodFallArr[i] = new SoundSample();
                    await this.SoundWoodFallArr[i].Init(
                        audioEngine.Context,
                        `woodFall` + i + `.mp3`,
                        audioEngine.MasterGain,
                        1.0,
                    );

                    if (this.SoundWoodFallArr[i].Gain && audioEngine.Convolver) {
                        this.SoundWoodFallArr[i].CreatePanner(audioEngine.Context, mainNode);
                        this.SoundWoodFallArr[i].Pan!.connect(audioEngine.Convolver);
                        //this.SoundWoodHitsArr[i].Gain!.connect(audioEngine.Convolver);
                    }
                }

                for (let i = 0; i < 7; i++) {
                    this.SoundPaintHitsArr[i] = new SoundSample();
                    await this.SoundPaintHitsArr[i].Init(
                        audioEngine.Context,
                        `paintHit` + i + `.mp3`,
                        audioEngine.MasterGain,
                        1.0,
                    );

                    if (this.SoundPaintHitsArr[i].Gain && audioEngine.Convolver) {
                        this.SoundPaintHitsArr[i].CreatePanner(audioEngine.Context, mainNode);
                        this.SoundPaintHitsArr[i].Pan!.connect(audioEngine.Convolver);
                        //this.SoundWoodHitsArr[i].Gain!.connect(audioEngine.Convolver);
                    }
                }

                for (let i = 0; i < 6; i++) {
                    this.SoundPaintSparksArr[i] = new SoundSample();
                    await this.SoundPaintSparksArr[i].Init(audioEngine.Context, `sparks` + i + `.mp3`, mainNode, 0.1);

                    /* if (this.SoundPaintSparksArr[i].Gain && audioEngine.Convolver) {
                        this.SoundPaintSparksArr[i].CreatePanner(audioEngine.Context, audioEngine.MasterGain);
                        this.SoundPaintSparksArr[i].Pan!.connect(audioEngine.Convolver);
                        //this.SoundWoodHitsArr[i].Gain!.connect(audioEngine.Convolver);
                    } */
                }

                {
                    await this.SoundLampHit.Init(audioEngine.Context, "lampHit.mp3", audioEngine.MasterGain, 1.0);
                    if (this.SoundLampHit.Gain && audioEngine.Convolver) {
                        this.SoundLampHit.CreatePanner(audioEngine.Context, mainNode);
                        this.SoundLampHit.Pan!.connect(audioEngine.Convolver);
                        //this.SoundWoodHitsArr[i].Gain!.connect(audioEngine.Convolver);
                    }
                }

                await this.SoundIngram.Init(audioEngine.Context, "ingramShot.mp3", mainNode, 0.45);
                if (this.SoundIngram.Gain) {
                    if (audioEngine.Convolver) {
                        this.SoundIngram.Gain.connect(audioEngine.Convolver);
                    }
                }
            }
        }
    }

    constructor(gl: WebGL2RenderingContext) {
        super();

        /* SceneBase.TextureEnvMapRect.InnerTexture = GTexturePool.CreateTexture(
            gl,
            false,
            "envmap9",
            true,
            true,
            false,
            SceneBase.TextureEnvMapRect,
        ); */

        this.TVSwitchButtonController = new SpatialControlPoint(GetVec2(0.85, -0.62), 0.1, false);

        this.SirenSwitchTimeLast[0] = 0.0;
        this.SirenSwitchTimeLast[1] = 0.15;
        this.SirenSwitchTimeLast[2] = 0.1;
        this.SirenRed[0] = false;
        this.SirenRed[1] = true;
        this.SirenRed[2] = false;

        GBulletSystem.GInstance = new GBulletSystem();

        this.SceneSDFDescShootingScene = InitSDFDestrPaintingScene(this);

        this.WallTiles = new DestructionTiles(gl, this);

        {
            const wallPlateSize = GStack.TempVec3;
            wallPlateSize.Set3(this.WallTiles.AreaSizeLength.x / 3, this.WallTiles.AreaSizeLength.y, 1.0);
            this.WallMetalStripAABB1.Position.x = -this.WallTiles.AreaSizeLength.x * 0.5 + wallPlateSize.x;
            this.WallMetalStripAABB1.Position.y = 2.75;
            this.WallMetalStripAABB1.Position.z = -0.03;

            this.WallMetalStripAABB2.Position.Set(this.WallMetalStripAABB1.Position);
            this.WallMetalStripAABB2.Position.x += wallPlateSize.x;

            this.WallMetalStripAABB1.Extent.x = this.WallTiles.TileSize.x / 7;
            this.WallMetalStripAABB1.Extent.y = this.WallTiles.AreaSizeLength.y * 0.5;
            this.WallMetalStripAABB1.Extent.z = 0.01;
            this.WallMetalStripAABB2.Extent = this.WallMetalStripAABB1.Extent;

            {
                const meshMetalStrip = new Mesh();
                meshMetalStrip.GeneratePlane(gl);
                meshMetalStrip.Scale.Set3(
                    this.WallMetalStripAABB1.Extent.x * 1.25,
                    this.WallMetalStripAABB1.Extent.y,
                    0.1,
                );
                meshMetalStrip.Position.Set(this.WallMetalStripAABB1.Position);
                const matName = `gold`;
                meshMetalStrip.Material.InitTextures(gl, matName + `DFS`, matName + `NRM`, matName + `RGH_R8`, false);
                meshMetalStrip.Material.TextureScale = 1.0;
                meshMetalStrip.Material.Metalness = 0.5;

                meshMetalStrip.Material.Metalness = 0.65;

                const meshMetalStrip2 = new Mesh();
                meshMetalStrip2.GeneratePlane(gl);
                meshMetalStrip2.Scale.Set(meshMetalStrip.Scale);
                meshMetalStrip2.Position.Set(this.WallMetalStripAABB2.Position);
                meshMetalStrip2.Material = meshMetalStrip.Material;

                this.AllMeshesArr.push(meshMetalStrip);
                this.AllMeshesArr.push(meshMetalStrip2);
            }
        }

        //Painting
        {
            this.PaintingPlane = new DestrPaintPlane(gl, GetVec2(512, 512 * (2.7 / 2.2)), this);

            const mesh = this.PaintingPlane.RenderMesh;
            mesh.Name = "Painting";
            mesh.GeneratePlane(gl);
            mesh.Position.Set3(0.0, 3.0, -0.1);
            mesh.Scale.Set3(2.2, 2.7, 1.0);
            mesh.Scale.x *= 0.9;
            mesh.Scale.y *= 0.9;

            const matName = `bluePaint`;
            mesh.Material.InitTextures(gl, matName + `DFS`, matName + `NRM`, matName + `RGH_R8`, false);
            mesh.Material.AlbedoTexture = GTexturePool.CreateTexture(
                gl,
                false,
                `paintingNicole1`,
                true,
                true,
                false,
                null,
                null,
            );

            //this.AllMeshesArr.push(mesh);
        }

        this.CityTexture = GTexturePool.CreateTexture(gl, false, `nySky3v`, true, true, false, null, null);
        this.FaceTexture = GTexturePool.CreateTexture(gl, false, `face2`, true, true, false, null, null);
        this.PaintProtectRune = GTexturePool.CreateTexture(gl, false, `rune4`, true, true, false, null, null);
        this.MovieTitleTexture = GTexturePool.CreateTexture(
            gl,
            false,
            `movieTitleTex_R8`,
            true,
            true,
            false,
            null,
            null,
        );
        this.MovieTitleSloganTexture = GTexturePool.CreateTexture(
            gl,
            false,
            `movieTitleSlogan`,
            true,
            true,
            false,
            null,
            null,
        );

        //Background
        {
            const meshBackground = new Mesh();
            meshBackground.GeneratePlane(gl);
            meshBackground.Scale.Set3(this.WallTiles.AreaSizeLength.x * 0.5, this.WallTiles.AreaSizeLength.y * 0.5, 1);
            meshBackground.Position.z = 0.05;
            meshBackground.Position.y = 2.75;
            const matName = `wall`;
            meshBackground.Material.InitTextures(gl, `payneClouds`, matName + `NRM`, matName + `RGH_R8`, false);
            //meshBackground.Material.AlbedoTexture = GTexturePool.CreateTexture(gl, false, `payneClouds`, true, true);
            //meshBackground.Material.bisEmmiter = true;
            meshBackground.Material.TextureScale = 3.0;
            meshBackground.Material.Brightness = 1.0;
            this.AllMeshesArr.push(meshBackground);
        }

        //message paint
        {
            const meshBackground = new Mesh();
            meshBackground.GeneratePlane(gl);
            //meshBackground.Scale.Set3(2.3317 * 1.0, 1.0, 1);
            meshBackground.Scale.Set3(2.1559 * 1.0, 1.0, 1);
            meshBackground.Position.x = -5.25;
            meshBackground.Position.y = 1.75;
            meshBackground.Position.z = 0.02;
            meshBackground.Orientation.y = 0.1;
            const matName = `gold`;
            meshBackground.Material.InitTextures(gl, `message3`, matName + `NRM`, matName + `RGH_R8`, false);
            meshBackground.Material.Brightness = 1.0;
            meshBackground.Material.bHasMask = true;
            this.AllMeshesArr.push(meshBackground);
            this.MessageMesh = meshBackground;
        }

        //heart paint
        {
            const meshBackground = new Mesh();
            meshBackground.GeneratePlane(gl);
            meshBackground.Position.Set3(4.15, 1.7, 0.02);
            meshBackground.Scale.Set3(1.93, 0.6, 0.1);
            const matName = `gold`;
            meshBackground.Material.InitTextures(gl, "heartTrail30", matName + `NRM`, matName + `RGH_R8`, false);
            meshBackground.Material.Brightness = 1.1;
            meshBackground.Material.bHasMask = true;
            meshBackground.Material.bHasMaskFromLuminance = true;
            //meshBackground.Material.bisEmmiter = true;
            this.AllMeshesArr.push(meshBackground);
            this.HeartMesh[1] = meshBackground;
        }
        {
            const meshBackground = new Mesh();
            meshBackground.GeneratePlane(gl);
            //meshBackground.Scale.Set3(2.3317 * 1.0, 1.0, 1);
            meshBackground.Scale.Set(this.HeartExtent);
            meshBackground.Scale.x *= 1.1;
            meshBackground.Position.Set(this.HeartCenterPos);
            //meshBackground.Position.y -= 1.0;
            meshBackground.Position.z = 0.02;
            const matName = `gold`;
            meshBackground.Scale.y *= this.HeartHeightScale;
            meshBackground.Material.InitTextures(gl, this.HeartTexName, matName + `NRM`, matName + `RGH_R8`, false);
            meshBackground.Material.Brightness = 1.25;
            meshBackground.Material.bHasMask = true;
            meshBackground.Material.bHasMaskFromLuminance = true;
            //meshBackground.Material.bisEmmiter = true;
            this.AllMeshesArr.push(meshBackground);
            this.HeartMesh[0] = meshBackground;
        }

        //name slab
        {
            const meshBackground = new Mesh();
            meshBackground.GeneratePlane(gl);
            meshBackground.Scale.Set3(5.0 * 0.15, 1.0 * 0.15, 1);
            meshBackground.Position.x = 0.0;
            meshBackground.Position.y = -0.13;
            meshBackground.Position.z = -0.02;
            const matName = `gold`;
            meshBackground.Material.InitTextures(gl, matName + `DFS`, matName + `NRM`, matName + `RGH_R8`, false);
            meshBackground.Material.Brightness = 1.0;
            meshBackground.Material.Metalness = 0.45;
            //meshBackground.Material.bHasMask = true;
            this.AllMeshesArr.push(meshBackground);
        }
        {
            const meshBackground = new Mesh();
            meshBackground.GeneratePlane(gl);
            meshBackground.Scale.Set3(5.0 * 0.1, 1.0 * 0.1, 1);
            meshBackground.Position.x = 0.0;
            meshBackground.Position.y = -0.13;
            meshBackground.Position.z = -0.025;
            const matName = `gold`;
            meshBackground.Material.InitTextures(gl, `dirName`, matName + `NRM`, matName + `RGH_R8`, false);
            meshBackground.Material.Brightness = 1.0;
            meshBackground.Material.bHasMask = true;
            this.AllMeshesArr.push(meshBackground);
        }

        this.SDFSceneRender = new SDFRender(gl, this.SceneSDFDescShootingScene);
        this.SDFSceneRender.ShaderVolumeLight = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetShaderSourceDestrSceneVolumeLightRenderPS(
                this.WallTiles.NumTiles,
                this.WallTiles.TileSize,
                this.SceneSDFDescShootingScene,
            ),
            GetSDFRenderUniformParametersList,
        );

        this.WallDecals = new Decals(gl, this, this.WallTiles);

        this.DustParticles = new DestructionParticles(gl, this, this.WallTiles, GetDustParticleDesc());
        this.SmokeParticles = new DestructionParticles(gl, this, this.WallTiles, GetSmokeParticleDesc());

        this.ImpactSmokeParticles = new DestructionParticles(gl, this, this.WallTiles, GetImpactSmokeParticleDesc());
        this.ImpactSparksParticles = new DestructionParticles(gl, this, this.WallTiles, GetImpactSparksParticleDesc());

        this.FlashParticles = new DestructionParticles(gl, this, this.WallTiles, GetFlashParticleDesc());

        this.SparksParticles = new DestructionParticles(gl, this, this.WallTiles, GetSparksParticleDesc());

        this.SnowParticles = new DestructionParticles(gl, this, this.WallTiles, GetSnowParticleDesc());
        //this.SnowFastParticles = new DestructionParticles(gl, this, this.WallTiles, GetSnowFastParticleDesc());
        this.SnowSmokeParticles = new DestructionParticles(gl, this, this.WallTiles, GetSnowSmokeParticleDesc());

        this.PaintSmokeParticles = new DestructionParticles(gl, this, this.WallTiles, GetPaintSmokeParticleDesc());
        this.PaintSparksParticles = new DestructionParticles(gl, this, this.WallTiles, GetPaintSparksParticleDesc());
        this.PaintSparksParticles2 = new DestructionParticles(gl, this, this.WallTiles, GetPaintSparksParticleDesc2());

        this.WoodDebrisParticles = new DestructionParticles(gl, this, this.WallTiles, GetWoodDebrisParticleDesc());
        this.WoodSmallChunkParticles = new DestructionParticles(
            gl,
            this,
            this.WallTiles,
            GetWoodSmallChunkParticleDesc(),
        );
        this.WoodMeshParticles = new DestructionParticles(gl, this, this.WallTiles, GetWoodMeshParticleDesc());
        this.WoodMeshParticles2 = new DestructionParticles(gl, this, this.WallTiles, GetWoodMesh2ParticleDesc());

        this.ShaderBasePassCurScene = new Shader(
            gl,
            GetShaderSourceBasePassMainVS(),
            GetShaderSourceBasePassMainPS(this.SceneSDFDescShootingScene),
            GetBasePassUniformParametersList,
        );

        this.ImpactComponent = new CImpactComponentBase();
        this.ImpactComponent.LoadResources(gl);

        this.ImpactComponentPainting = new PaintDestrImpactComponent();
        this.ImpactComponentPainting.LoadResources(gl);

        GBulletSystem.GInstance!.bIntersectionThisFrame = true;
        this.WallTiles.OnUpdate(gl);
        GBulletSystem.GInstance!.bIntersectionThisFrame = false;

        this.SceneSDFDescTVScene = InitSDFTVScene();
        this.ShaderTVScene = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetShaderSourceTVScenePS(this.SceneSDFDescTVScene),
            GetSDFRenderUniformParametersList,
        );

        this.LoadSounds();
        this.InitOsc();
    }

    TimeSinceFirstRender = 0.0;
    TimeAfterPaintDeath = 0.0;

    static setMainTitleSubVisibility: React.Dispatch<React.SetStateAction<boolean>>;
    static setHealthbarVisibility: React.Dispatch<React.SetStateAction<boolean>>;
    static setHealthbarCur: React.Dispatch<React.SetStateAction<number>>;

    static setAlanSubVisibility: React.Dispatch<React.SetStateAction<boolean>>;
    static setAlanSubNum: React.Dispatch<React.SetStateAction<number>>;
    static setMaxSubVisibility: React.Dispatch<React.SetStateAction<boolean>>;
    static setMaxSubNum: React.Dispatch<React.SetStateAction<number>>;

    Render(gl: WebGL2RenderingContext) {
        const bulletSystem = GBulletSystem.GInstance!;
        const bRenderSnow = 0 || this.PaintState >= EPaintingState.Dead;
        const bRenderPaintImpactSparks = true;
        const bRenderPaintImpactDensity = this.PaintState != 0;
        const bRenderPaintImpactImpulse = true;

        const buf = SceneBase.UniformBuffer.Params;

        //Update
        GBulletSystem.GInstance!.OnUpdate(gl, this);

        GCameraShakeController.OnUpdate();

        DestrPaintOnUpdateScript(gl, this);

        super.OnUpdate(gl);

        if (!this.bTVRenderState) {
            if (
                bulletSystem.bSceneObjectIntersectionThisFrame &&
                this.PaintState != EPaintingState.HealProtect &&
                this.PaintState != EPaintingState.HeartCanShoot &&
                this.PaintState != EPaintingState.HeartVisible
            ) {
                this.PaintingPlane.BindFireRT(gl);
                this.PaintingPlane.ApplyFire(gl, GBulletSystem.GInstance!.IntersectionPos, this);
            }

            //Update Mesh
            {
                const paint = this.PaintingPlane;
                paint.RenderMesh.RegenerateMatrix();
                if (this.PaintState == EPaintingState.HealProtect) {
                    paint.PaintBrightnessScale = Math.max(0.7, paint.PaintBrightnessScale - GTime.Delta * 1.5);
                }
                paint.OnUpdate(gl, this);
                const curFireTexture = paint.FireTexture[paint.PingPongTextureCurIndex];
                gl.bindTexture(gl.TEXTURE_2D, curFireTexture);
                gl.generateMipmap(gl.TEXTURE_2D);
            }
        }

        if (
            (GBulletSystem.GInstance!.bSceneObjectIntersectionThisFrame ||
                GBulletSystem.GInstance!.bLampIntersectionThisFrame) &&
            bRenderPaintImpactImpulse
        ) {
            const ndcPos = GStack.TempVec3_2;
            ndcPos.Set(GBulletSystem.GInstance!.IntersectionPos);
            ndcPos.Negate(buf.CameraPosition);
            ndcPos.x *= buf.CameraZoom.x;
            ndcPos.y *= buf.CameraZoom.x;
            ndcPos.x /= buf.ScreenRatio.x;
            ndcPos.x /= 0.1 + ndcPos.z;
            ndcPos.y /= 0.1 + ndcPos.z;

            GVectorFieldInjector.GInstance?.InjectImpulse(
                gl,
                VectorField.GFullscreenInstance!,
                GetVec2(ndcPos.x, ndcPos.y),
            );
        }

        if (!this.bTVRenderState) {
            this.WallTiles.OnUpdate(gl);
            this.WallDecals.OnUpdate(gl);

            this.DustParticles.OnUpdate(gl, this.WallTiles, 4);
            this.SmokeParticles.OnUpdate(
                gl,
                this.WallTiles,
                9,
                bulletSystem.bIntersectionThisFrame || bulletSystem.bLampIntersectionThisFrame,
            );

            this.FlashParticles.OnUpdate(
                gl,
                this.WallTiles,
                4,
                bulletSystem.bIntersectionThisFrame || bulletSystem.bSceneObjectIntersectionThisFrame,
            );
            this.SparksParticles.OnUpdate(gl, this.WallTiles, 5, bulletSystem.bLampIntersectionThisFrame);

            this.PaintSmokeParticles.OnUpdate(
                gl,
                this.WallTiles,
                9,
                bulletSystem.bSceneObjectIntersectionThisFrame && this.PaintState == 0,
            );

            if (bRenderPaintImpactSparks) {
                this.PaintSparksParticles.OnUpdate(
                    gl,
                    this.WallTiles,
                    4,
                    bulletSystem.bSceneObjectIntersectionThisFrame || bulletSystem.bLampIntersectionThisFrame,
                );
                this.PaintSparksParticles2.OnUpdate(
                    gl,
                    this.WallTiles,
                    4,
                    bulletSystem.bSceneObjectIntersectionThisFrame || bulletSystem.bLampIntersectionThisFrame,
                );
            }

            this.WoodDebrisParticles.OnUpdate(gl, this.WallTiles, 0);
            this.WoodSmallChunkParticles.OnUpdate(gl, this.WallTiles, 1);
            this.WoodMeshParticles.OnUpdate(gl, this.WallTiles, 2);
            this.WoodMeshParticles2.OnUpdate(gl, this.WallTiles, 3);
        }

        if (bRenderSnow) {
            const curDelta = GTime.Cur - this.SnowSmokeParticles.LastSpawnTime;
            const bCanSpawn = curDelta > this.SnowSmokeParticles.Desc.SpawnTimeDelta;
            this.SnowSmokeParticles.OnUpdate(gl, this.WallTiles, 14, bCanSpawn);

            this.SnowParticles.OnUpdate(gl, this.WallTiles, 14, true);
            //this.SnowFastParticles.OnUpdate(gl, this.WallTiles, 14, true);
        }

        for (const meshRef of this.AllMeshesArr) {
            meshRef.RegenerateMatrix();
        }

        //Render
        if (this.bTVRenderState) {
            BindRenderTarget(
                gl,
                GRenderTargets.SceneColorFramebufferCopy!,
                GScreenDesc.RenderTargetSize,
                true,
                true,
                true,
            );
        } else {
            BindRenderTarget(gl, GRenderTargets.SceneColorFramebuffer!, GScreenDesc.RenderTargetSize, true, true, true);
        }

        if (!this.bTVRenderState) {
            {
                const pos = GStack.TempVec3;
                pos.Set(this.PaintingAABB.Position);
                pos.z = 0.3;
                gl.enable(gl.STENCIL_TEST);
                gl.stencilOp(gl.REPLACE, gl.REPLACE, gl.REPLACE);
                gl.stencilMask(0x1);
                gl.stencilFunc(gl.ALWAYS, 3, 0xff);
                gl.colorMask(false, false, false, false);
                const size = GStack.TempVec3_7;
                size.Set3(1.98, 2.45, 0);
                const col = GStack.TempVec3_6;
                col.Set3(1, 0, 0);
                GSimpleShapesRenderer.GInstance!.RenderPoint(gl, pos, size, col, false, undefined);

                gl.stencilFunc(gl.NOTEQUAL, 3, 0x1);
                gl.stencilOp(gl.KEEP, gl.REPLACE, gl.REPLACE);
                gl.stencilMask(0x2);
                //gl.disable(gl.STENCIL_TEST);
                gl.colorMask(true, true, true, true);
            }

            gl.enable(gl.DEPTH_TEST);
            gl.depthFunc(gl.LESS);

            gl.enable(gl.CULL_FACE);
            gl.cullFace(gl.BACK);

            //gl.enable(gl.STENCIL_TEST);

            this.WallTiles.Render(gl);

            gl.disable(gl.DEPTH_TEST);
            gl.disable(gl.CULL_FACE);

            this.WallDecals.Render(gl, this.WallTiles);

            gl.disable(gl.STENCIL_TEST);

            gl.enable(gl.DEPTH_TEST);

            //Particles
            {
                this.WoodDebrisParticles.Render(gl, 0);
                this.WoodSmallChunkParticles.Render(gl, 1);
                this.WoodMeshParticles.Render(gl, 2);
                this.WoodMeshParticles2.Render(gl, 3);

                this.FlashParticles.Render(gl, 7);

                if (bRenderPaintImpactSparks) {
                    if (this.PaintState >= EPaintingState.StartDeath) {
                        this.PaintSparksParticles.Render(gl, 7);
                    }
                    this.PaintSparksParticles2.Render(gl, 17);
                }

                this.SparksParticles.Render(gl, 7);
            }
        }

        {
            const basePass = GBasePass.GInstance!;
            basePass.Begin(gl, this.ShaderBasePassCurScene!);

            if (!this.bTVRenderState) {
                gl.enable(gl.STENCIL_TEST);
                //Render Meshes
                for (const meshRef of this.AllMeshesArr) {
                    basePass.RenderMesh(
                        gl,
                        this.ShaderBasePassCurScene!,
                        meshRef,
                        SceneBase.TextureEnvMapWithMips,
                        1,
                        0,
                    );
                }

                gl.stencilFunc(gl.ALWAYS, 3, 0xff);
                gl.stencilMask(0x2);

                this.PaintingPlane.Render(gl, SceneBase.TextureEnvMapWithMips);

                gl.disable(gl.STENCIL_TEST);
            }

            const cityDepthOffset = 10.0;
            {
                const pos = GStack.TempVec3_7;
                pos.Set3(0.0, 2.0, 100.0 + cityDepthOffset);
                const size = GStack.TempVec3_6;
                size.Set3(1.7 * 50.0, 50.0, 0);
                const col = GStack.TempVec3_5;
                const bright = 0.3;
                col.SetSame(bright);
                GSimpleShapesRenderer.GInstance!.RenderPoint(gl, pos, size, col, false, undefined, this.CityTexture);
            }

            basePass.End(gl);

            if (
                this.PaintState == EPaintingState.HealProtect ||
                this.PaintState == EPaintingState.HeartVisible ||
                this.PaintState == EPaintingState.HeartCanShoot /* ||
                this.PaintState == EPaintingState.StartDeath ||
                this.PaintState == EPaintingState.Dead */
            ) {
                if (bulletSystem.bSceneObjectIntersectionThisFrame) {
                    this.RuneBrightnessScale = 1.0;
                }

                gl.enable(gl.BLEND);
                gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
                gl.blendEquation(gl.FUNC_ADD);
                const bright = 0.5;
                const pos = GStack.TempVec3;
                pos.Set3(0.0, 3.0, 0.0);
                const size = GStack.TempVec3_2;
                size.Set3(1.75, 1.5, 0);
                const col = GStack.TempVec3_3;
                col.Set3(
                    bright * this.RuneBrightnessScale,
                    1.0 * this.RuneBrightnessScale,
                    bright * this.RuneBrightnessScale,
                );
                GSimpleShapesRenderer.GInstance!.RenderPoint(
                    gl,
                    pos,
                    size,
                    col,
                    false,
                    undefined,
                    this.PaintProtectRune,
                    true,
                    true,
                );
                gl.disable(gl.BLEND);

                this.RuneBrightnessScale = Math.max(0.02, (this.RuneBrightnessScale -= GTime.Delta));
            }

            if (bRenderSnow) {
                gl.enable(gl.STENCIL_TEST);

                gl.stencilFunc(gl.NOTEQUAL, 3, 0x2);
                gl.stencilOp(gl.KEEP, gl.KEEP, gl.KEEP);
                gl.stencilMask(0x0);

                const towerFlashBr = 0.5 + (Math.sin(GTime.Cur * Math.PI * 2.0 * 0.67) * 0.5 + 0.5) * 0.8 + 0.2;

                const colorRed = GStack.TempVec3_3;
                colorRed.Set3(Math.max(0.5, towerFlashBr), 0.0, 0.0);
                const pos = GStack.TempVec3_2;
                pos.Set3(-0.5, 19.0, 99.5 + cityDepthOffset);
                gl.enable(gl.BLEND);
                gl.blendFunc(gl.ONE, gl.ONE);
                gl.blendEquation(gl.FUNC_ADD);
                //gl.depthMask(false);

                GSimpleShapesRenderer.GInstance!.RenderPoint(
                    gl,
                    pos,
                    0.75,
                    colorRed,
                    false,
                    0,
                    this.ImpactComponentPainting.LightFlareTexture,
                );
                const flareSize = 10.0;
                const fS = GStack.TempVec3_7;
                fS.Set3(flareSize * 2.0 * Math.max(1.0, towerFlashBr), flareSize, 0.0);
                GSimpleShapesRenderer.GInstance!.RenderPoint(
                    gl,
                    pos,
                    fS,
                    colorRed,
                    false,
                    0.0,
                    this.ImpactComponent.LightFlareTexture,
                );

                gl.disable(gl.BLEND);
                gl.disable(gl.STENCIL_TEST);

                gl.enable(gl.DEPTH_TEST);
                this.SnowParticles.Render(gl, 4);
                this.SnowSmokeParticles.Render(gl, 4);
                gl.disable(gl.DEPTH_TEST);

                if (GSceneDesc.Camera.Position.z > -0.5) {
                    if (!this.bMovieTitleShown) {
                        const audioContext = GAudioEngine.Instance!.Context;
                        if (audioContext) {
                            this.SoundBellHit.Play(audioContext, false);
                        }
                        this.bMovieTitleShown = true;
                    }

                    gl.enable(gl.BLEND);
                    const titlePos = GStack.TempVec3_7;
                    titlePos.Set3(0.0, GSceneDesc.Camera.Position.y - 0.01, GSceneDesc.Camera.Position.z + 1.0);
                    const size = GStack.TempVec3_6;
                    const ss = 0.19;
                    size.Set3(1.97 * ss, ss, 0);
                    const col = GStack.TempVec3_5;
                    const br = 0.8;
                    const grblAm = 0.4;
                    col.Set3(0.60546875 * br, 0.10546875 * br * grblAm, 0.11328125 * br * grblAm);
                    GSimpleShapesRenderer.GInstance!.RenderPoint(
                        gl,
                        titlePos,
                        size,
                        col,
                        false,
                        undefined,
                        this.MovieTitleTexture,
                        true,
                        true,
                    );

                    {
                        col.SetSame(0.45);
                        size.Set3(13.433 * 0.012, 0.012, 0);
                        titlePos.y += 0.3;
                        GSimpleShapesRenderer.GInstance!.RenderPoint(
                            gl,
                            titlePos,
                            size,
                            col,
                            false,
                            undefined,
                            this.MovieTitleSloganTexture,
                            true,
                            true,
                        );
                    }
                }
            }
        }

        //this.SnowParticles.Render(gl, 4);

        if (!this.bTVRenderState) {
            if (this.PaintState != EPaintingState.AfterDeath) {
                GBulletSystem.GInstance!.Render(gl, this);
            }

            {
                const color = GStack.TempVec3_3;
                color.Set3(0.7, 0.8, 1.0);
                this.ImpactComponent.UpdateAndRender(gl, color);

                this.ImpactComponentPainting.UpdateAndRenderForScene(gl, this);
            }

            {
                gl.enable(gl.BLEND);
                gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
                gl.blendEquation(gl.FUNC_ADD);
                this.DustParticles.Render(gl, 4);
                gl.disable(gl.BLEND);
            }

            //Smoke
            {
                BindRenderTarget(
                    gl,
                    GRenderTargets.SmokeFramebuffer!,
                    GScreenDesc.HalfResRenderTargetSize,
                    true,
                    false,
                    false,
                );

                gl.disable(gl.DEPTH_TEST);
                gl.disable(gl.CULL_FACE);

                //this.DustParticles.Render(gl, 4);
                this.SmokeParticles.Render(gl, 4);
                this.PaintSmokeParticles.Render(gl, 4);

                if (this.ImpactComponentPainting.TimeSinceStart < 30) {
                    this.ImpactSmokeParticles.Render(gl, 4);
                }
            }
        }

        if (
            (bulletSystem.NumTimesHeartAreaShot > 60 || this.PaintState == EPaintingState.HeartCanShoot) &&
            this.PaintState != EPaintingState.AfterDeath
        ) {
            const bBeat = Math.sin(GTime.Cur * Math.PI * 2.0 * 0.6) > 0.999;

            if (bBeat || bulletSystem.bHeartIntersectionThisFrame) {
                if (bBeat) {
                    const audioContext = GAudioEngine.Instance!.Context;
                    if (audioContext) {
                        PayneSceneLightSwitch.GInstance!.SoundHeartbeat.Play(audioContext, true);
                    }
                }

                const col = GStack.TempVec3_5;
                const bright = 0.4;
                col.Set3(bright, 0.0, 0.0);
                const size = GStack.TempVec3_6;
                size.Set(this.HeartExtent);
                size.Mul(0.2);
                //GSimpleShapesRenderer.GInstance!.RenderPoint(gl, this.HeartCenterPos, this.HeartExtent, col);
                {
                    const ndcPos = GStack.TempVec3_2;
                    ndcPos.Set(this.HeartCenterPos);
                    ndcPos.x -= 0.2;
                    ndcPos.y -= 0.2;
                    if (bulletSystem.bHeartIntersectionThisFrame) {
                        ndcPos.Set(bulletSystem.HeartIntersectionPos);
                    }

                    ndcPos.Negate(buf.CameraPosition);
                    ndcPos.x *= buf.CameraZoom.x;
                    ndcPos.y *= buf.CameraZoom.x;
                    ndcPos.x /= buf.ScreenRatio.x;
                    ndcPos.x /= 0.1 + ndcPos.z;
                    ndcPos.y /= 0.1 + ndcPos.z;

                    GVectorFieldInjector.GInstance?.InjectImpulse(
                        gl,
                        VectorField.GFullscreenInstance!,
                        GetVec2(ndcPos.x, ndcPos.y),
                    );
                }
            }
        }

        //Density
        if (!this.bTVRenderState) {
            VectorField.GFullscreenInstance!.BindCurDensityRT(gl);

            const bBlend = true;

            if (bBlend) {
                gl.enable(gl.BLEND);
                gl.blendFunc(gl.ONE, gl.ONE);
                gl.blendEquation(gl.MAX);
            }

            if (GBulletSystem.GInstance!.bSceneObjectIntersectionThisFrame /* && bRenderPaintImpactDensity */) {
                const col = GStack.TempVec3_4;
                col.Set3(0.0, 1.0 * this.PaintDensityBrightness * 1.0, 1.0 * 0.2 * this.PaintDensityBrightness);
                GSimpleShapesRenderer.GInstance!.RenderPoint(gl, buf.IntersectionPosWS, Math.random() * 0.1, col, true);
            }
            if (GBulletSystem.GInstance!.bHeartIntersectionThisFrame) {
                const col = GStack.TempVec3_4;
                col.Set3(0.95, 0.0, 0.0);
                GSimpleShapesRenderer.GInstance!.RenderPoint(
                    gl,
                    bulletSystem.HeartIntersectionPos,
                    Math.random() * 0.065,
                    col,
                    true,
                );
            }

            if (GBulletSystem.GInstance!.bLampIntersectionThisFrame) {
                const col = GStack.TempVec3_4;
                col.Set3(0.7, 0.5, 0.9);
                GSimpleShapesRenderer.GInstance!.RenderPoint(gl, buf.IntersectionPosWS, 0.25, col, true);
            }

            if (this.PaintState == EPaintingState.StartDeath || this.PaintState == EPaintingState.Dead) {
                const bright = 0.3;
                const pos = GStack.TempVec3_7;
                pos.Set(this.HeartCenterPos);
                const size = GStack.TempVec3_6;
                size.Set(this.HeartExtent);
                size.x *= 1.1;
                const col = GStack.TempVec3_5;
                col.Set3(bright, 0.5, 0.5);
                GSimpleShapesRenderer.GInstance!.RenderPoint(
                    gl,
                    pos,
                    size,
                    col,
                    false,
                    undefined,
                    this.HeartMesh[0].Material.AlbedoTexture,
                    false,
                    true,
                );
            }

            /* {
                const colorRed = GStack.TempVec3_3;
                colorRed.Set3(1.0, 0.0, 0.0);
                const pos = GStack.TempVec3_2;
                pos.Set3(-0.5, 19.0, 99.5);

                GSimpleShapesRenderer.GInstance!.RenderPoint(gl, pos, 0.2, colorRed, true);
            } */

            if (bBlend) {
                gl.disable(gl.BLEND);
            }
        }

        const bullets = GBulletSystem.GInstance!;

        if (bullets.BulletTimeState > 0) {
            bullets.BulletSpeed = 80;
            GTime.DeltaTimeScale = 0.4;
            this.PaintDensityBrightness = 0.25;

            const blinkDuration = 0.25 * 0.5;
            const minExposure = 0.6;
            if (bullets.TimeSinceBulletTimeEnabled < blinkDuration) {
                if (bullets.TimeSinceBulletTimeEnabled <= blinkDuration * 0.5) {
                    const t = Math.max(
                        0.0,
                        MathMapToRange(bullets.TimeSinceBulletTimeEnabled, 0.0, blinkDuration * 0.5, 1.0, minExposure),
                    );
                    RCombinerPass.Exposure = 1.2 * t;
                } else {
                    const t = MathClamp(
                        MathMapToRange(
                            bullets.TimeSinceBulletTimeEnabled,
                            blinkDuration * 0.5,
                            blinkDuration,
                            minExposure,
                            1.0,
                        ),
                        0.0,
                        1.0,
                    );
                    RCombinerPass.Exposure = 1.2 * t;
                }
            }

            if (bullets.TimeSinceBulletTimeEnabled > 1.0) {
                const audioContext = GAudioEngine.Instance!.Context;
                if (audioContext && bullets.BulletTimeState == EBulletTimeState.Enabled) {
                    PayneSceneLightSwitch.GInstance!.SoundHeartbeat.Play(audioContext, true);
                }
            }
        } else {
            GTime.DeltaTimeScale = 1.0;
            this.PaintDensityBrightness = bRenderPaintImpactDensity ? 2.0 : 0.5;
            bullets.BulletSpeed = bullets.kBulletMaxSpeed;
        }

        /* if (GBulletSystem.GInstance!.BulletTimeTriggerParam > GBulletSystem.GInstance!.kBulletTimeStartThres) {
            this.BulletTimeEndParam = 0.0;
            GTime.DeltaTimeScale = 0.4;
            this.PaintDensityBrightness = 0.05;

            const blinkDuration = 0.25 * 0.5;
            const minExposure = 0.6;
            if (this.BulletTimeStartParam < blinkDuration) {
                if (blinkDuration <= blinkDuration * 0.5) {
                    const t = Math.max(
                        0.0,
                        MathMapToRange(this.BulletTimeStartParam, 0.0, blinkDuration * 0.5, 1.0, minExposure),
                    );
                    RCombinerPass.Exposure = 1.2 * t;
                } else {
                    const t = MathClamp(
                        MathMapToRange(this.BulletTimeStartParam, blinkDuration * 0.5, blinkDuration, minExposure, 1.0),
                        0.0,
                        1.0,
                    );
                    RCombinerPass.Exposure = 1.2 * t;
                }
            }

            const distDur = 1.0;
            if (this.BulletTimeStartParam < distDur) {
                const t = this.BulletTimeStartParam;
                RCombinerPass.FisheyeParam = MathImpulse(t, 2.0) * -0.025;
            }

            this.BulletTimeStartParam += GTime.Delta;
        } else if (GBulletSystem.GInstance!.BulletTimeTriggerParam < GBulletSystem.GInstance!.kBulletTimeEndThres) {
            GTime.DeltaTimeScale = 1.0;
            this.PaintDensityBrightness = 1.0;

            const distDur = 2.0;
            if (this.BulletTimeEndParam < distDur) {
                const t = this.BulletTimeEndParam;
                RCombinerPass.FisheyeParam = MathImpulse(t, 2.0) * 0.2;
            }

            this.BulletTimeEndParam += GTime.Delta;
        } */

        this.PaintingPlane.LoadCurValuesToCPU(gl);

        const health = MathClamp(1.0 - this.PaintingPlane.FireValueReadData.CurValueCPU * 2.0, 0.0, 1.0) * 100.0;

        if (
            this.PaintState == EPaintingState.HealProtect ||
            this.PaintState == EPaintingState.HeartVisible ||
            this.PaintState == EPaintingState.HeartCanShoot
        ) {
            if (bulletSystem.bSceneObjectIntersectionThisFrame || bulletSystem.bHeartIntersectionThisFrame) {
                this.SoundOscGainScaleParam = 1.0;
            }

            if (this.SoundOscGain) {
                this.SoundOscGain.gain.value = this.SoundOscGainInitValue * this.SoundOscGainScaleParam;
                this.SoundOscGainScaleParam = Math.max(0.0, this.SoundOscGainScaleParam - GTime.Delta);
            }
        }

        if (this.PaintState >= EPaintingState.StartDeath) {
            this.SoundOscGainScaleParam = 1.0;
            if (this.SoundOscGain) {
                this.SoundOscGain.gain.value = this.SoundOscGainInitValue * this.SoundOscGainScaleParam;
            }

            if (this.SoundOsc0) {
                this.SoundOsc0.frequency.value = MathLerp(
                    400.0,
                    50.0,
                    Math.pow(MathClamp(health * 0.01, 0.0, 1.0), 2.0),
                );
                this.SoundOsc1!.frequency.value = MathLerp(80.0, 60.0, MathClamp(health * 0.01, 0.0, 1.0));
            }
            if (this.SoundOscFilter) {
                this.SoundOscFilter.frequency.value = MathLerp(250.0, 200.0, MathClamp(health * 0.01, 0.0, 1.0));
            }

            if (this.PaintState > EPaintingState.StartDeath) {
                const audioContext = GAudioEngine.Instance!.Context;
                if (audioContext) {
                    PayneSceneLightSwitch.GInstance!.SoundRoomNoise.Play(audioContext, true);
                    if (PayneSceneLightSwitch.GInstance!.SoundRoomNoise.Gain) {
                        PayneSceneLightSwitch.GInstance!.SoundRoomNoise.Gain.gain.value =
                            0.25 * (1.0 - MathClamp(health * 0.01, 0.0, 1.0));
                    }
                }
            }
        }

        if (this.PaintState == EPaintingState.InitNoHeal) {
            if (health < 75) {
                this.PaintState = EPaintingState.HealProtect;
                PayneSceneDestrPainting.setAlanSubVisibility(true);
                GCameraShakeController.ShakeCameraFast(0.1 * 5.0);

                const audioContext = GAudioEngine.Instance!.Context;
                if (audioContext) {
                    this.SoundBellHit.Play(audioContext);
                    this.StartSynthSound();
                }
            }
        }
        PayneSceneDestrPainting.setHealthbarCur(health);

        if (health < 50 && this.PaintState == EPaintingState.StartDeath) {
            this.PaintState = EPaintingState.Dead;
            GTime.DeltaTimeScale = 1.0;
            this.ImpactComponentPainting.OnHit(GetVec3(0.0, 3.25, -0.1));
        }

        if (this.bTVRenderState) {
            const curShader = this.ShaderTVScene;

            gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);
            gl.useProgram(curShader.ShaderProgram);

            BindRenderTarget(gl, GRenderTargets.SceneColorFramebuffer!, GScreenDesc.RenderTargetSize, true, true, true);
            GLBindUniformBuffer(
                gl,
                SceneBase.UniformBuffer.BufferGPU!,
                curShader.ShaderProgram,
                curShader.UniformParametersLocationList.SceneUniformBuffer,
            );

            GLSetTexture(
                gl,
                curShader.UniformParametersLocationList.ProjectorTexture,
                this.ETVSwitchState == 2 ? this.FaceTexture : GRenderTargets.SceneColorTextureCopy,
                1,
            );

            GLSetTexture(
                gl,
                curShader.UniformParametersLocationList.ProjectorTexture,
                this.ETVSwitchState == 2 ? this.FaceTexture : GRenderTargets.SceneColorTextureCopy,
                2,
            );

            GLSetTextureCubemap(
                gl,
                curShader.UniformParametersLocationList.CubemapTexture,
                SceneBase.TextureEnvMapWithMips,
                4,
            );

            gl.uniform1i(curShader.UniformParametersLocationList.ETVSwitchState, this.ETVSwitchState);

            gl.drawArrays(gl.TRIANGLES, 0, 3);

            if (this.ETVSwitchState < 2) {
                this.TVSwitchButtonController.OnUpdate();

                if (this.TVSwitchButtonController.bIntersectionThisFrame) {
                    this.ETVSwitchState = 1;
                    if (GUserInputDesc.bPointerInputPressedCurFrame) {
                        this.ETVSwitchState = 2;

                        GHallwaySceneCSS.SetNewPage(4);

                        this.bBackFilterEnabled = false;
                        if (this.SoundMainTheme1.bIsPlaying) {
                            this.SoundMainTheme1.Stop();
                        }
                        if (this.SoundMainTheme0.bIsPlaying) {
                            this.SoundMainTheme0.Stop();
                        }
                        const audioContext = GAudioEngine.Instance!.Context;
                        if (audioContext) {
                            this.SoundTVSwitch.Play(audioContext, false);
                        }
                    }
                } else {
                    this.ETVSwitchState = 0;
                }
                if (this.ETVSwitchState == 2) {
                    PayneSceneDestrPainting.setMaxSubVisibility(false);
                    PayneSceneDestrPainting.setMaxSubNum(3);
                }
                /* {
                    const col = GStack.TempVec3;
                    col.Set3(1.0, 0.0, 1.0);
                    if (this.TVSwitchButtonController.bIntersectionThisFrame) {
                        col.y = 1.0;
                    }
                    const pos = GStack.TempVec3_2;
                    pos.x = this.TVSwitchButtonController.PositionViewSpace.x + GSceneDesc.Camera.Position.x;
                    pos.y = this.TVSwitchButtonController.PositionViewSpace.y + GSceneDesc.Camera.Position.y;
                    pos.z = GSceneDesc.Camera.Position.z + 2.5;
                    GSimpleShapesRenderer.GInstance!.RenderPoint(
                        gl,
                        pos,
                        this.TVSwitchButtonController.Radius,
                        col,
                        false,
                        undefined,
                    );
                } */
            }
        }
    } //Render()
}
